import { HUD } from '../../config/tuning';
import type { HudPainter } from '../HudLayout';
import { TextSlot } from '../HudLayout';
import type { HudFrame } from '../HudFrame';

/**
 * The numeric readouts (SPEC §3.4.1): airspeed left, altitude right, load factor below it, and
 * endurance bottom left.
 *
 * The warning colours are the only place the display leaves cyan apart from the designation
 * mark, and each threshold is a specific piece of information rather than decoration: amber
 * airspeed means the stall margin is thinning *at the current load factor*, so it moves the
 * moment the player pulls, which is precisely when it matters.
 */

/**
 * Airspeed colour band. Exported so the thresholds can be tested without a canvas.
 *
 * Both ends of the envelope, on the one number they belong to. The overspeed end was missing
 * entirely: past V_NE the airframe erodes until the engine quits, and the only feedback the
 * player got was the *endurance* readout reaching zero — a fuel indication for a structural
 * death. Red at V_NE rather than amber-then-red because, unlike the stall, there is no margin
 * band worth drawing: the damage starts at the line.
 */
export function airspeedColour(
  airspeedMs: number,
  stallSpeedMs: number,
  vneMs: number,
): string | undefined {
  if (airspeedMs < stallSpeedMs) return HUD.dangerColour;
  if (airspeedMs > vneMs) return HUD.dangerColour;
  if (airspeedMs < stallSpeedMs * HUD.stallWarnFactor) return HUD.warnColour;
  if (airspeedMs > vneMs * HUD.overspeedWarnFactor) return HUD.warnColour;
  return undefined;
}

/** Altitude colour band, from radar altitude AGL. */
export function altitudeColour(altitudeAglM: number): string | undefined {
  if (altitudeAglM < HUD.criticalAltM) return HUD.dangerColour;
  if (altitudeAglM < HUD.lowAltWarnM) return HUD.warnColour;
  return undefined;
}

/** Load factor colour band. */
export function loadFactorColour(loadFactor: number): string | undefined {
  return Math.abs(loadFactor) > HUD.gWarn ? HUD.dangerColour : undefined;
}

/**
 * Commanded throttle as a whole percentage, e.g. `55%`.
 *
 * Memoised on the rounded value, like `HudPainter.number`, because unlike the load-factor readout
 * this one is on screen every frame: composing the string inline would allocate 120 short-lived
 * strings a second inside the render loop, which is what CLAUDE.md rules out.
 */
export function throttleText(throttle: number): string {
  const clamped = throttle < 0 ? 0 : throttle > 1 ? 1 : throttle;
  const percent = Math.round(clamped * 100);
  if (percent !== throttleCache.percent) {
    throttleCache.percent = percent;
    throttleCache.text = `${percent}%`;
  }
  return throttleCache.text;
}

const throttleCache = { percent: -1, text: '' };

export function drawReadouts(painter: HudPainter, frame: HudFrame): void {
  const layout = painter.layout;
  const margin = layout.px(HUD.marginPx);
  const gap = layout.px(HUD.readoutGapPx);

  // Airspeed, left of centre.
  painter.text(
    painter.number(TextSlot.Airspeed, frame.airspeedMs, 0),
    margin,
    layout.centreY,
    'left',
    airspeedColour(frame.airspeedMs, frame.stallSpeedMs, frame.vneMs),
  );
  painter.text(
    frame.airspeedMs > frame.vneMs ? 'M/S  V-NE' : 'M/S',
    margin,
    layout.centreY + gap,
    'left',
  );

  // Commanded throttle, below the airspeed and mirroring the load factor on the other side. The
  // lever is otherwise invisible: it is set by a key that commands a *rate*, so without this the
  // only way to know where it is set is to infer it from the speed it eventually produces.
  // Label and value are drawn separately so neither call composes a string: both are constants as
  // far as this frame is concerned, one static and one memoised.
  const throttleY = layout.centreY + gap * HUD.readoutRowGapFactor;
  painter.text('THR', margin, throttleY, 'left');
  painter.text(throttleText(frame.throttle), margin + layout.px(HUD.throttleValuePx), throttleY, 'left');

  // Altitude, right of centre.
  const rightEdge = layout.widthPx - margin;
  painter.text(
    painter.number(TextSlot.Altitude, frame.altitudeAglM, 0),
    rightEdge,
    layout.centreY,
    'right',
    altitudeColour(frame.altitudeAglM),
  );
  painter.text('M AGL', rightEdge, layout.centreY + gap, 'right');

  // Load factor, below altitude, only when it is worth reading.
  if (Math.abs(frame.loadFactor) > HUD.loadFactorShowAbove) {
    painter.text(
      `${painter.number(TextSlot.LoadFactor, frame.loadFactor, 1)}G`,
      rightEdge,
      layout.centreY + gap * HUD.readoutRowGapFactor,
      'right',
      loadFactorColour(frame.loadFactor),
    );
  }

  // Endurance, bottom left.
  painter.text(
    painter.duration(TextSlot.Endurance, frame.enduranceS),
    margin,
    layout.heightPx - margin,
    'left',
  );
  painter.text('ENDUR', margin, layout.heightPx - margin - gap, 'left');
}
